import type { EventFormValues } from "@/types/records";
import { Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EventAttachmentsManager } from "@/components/producer/EventAttachmentsManager";
import { InlineImageCrop } from "@/components/producer/InlineImageCrop";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { RichTextEditor } from "@/components/producer/RichTextEditor";
import { getCategoryLabel } from "@/lib/categories";
import { useEventCategories } from "@/hooks/useEventCategories";
import { getEventPublicPath } from "@/lib/urls";
import { BRAZIL_EVENT_TIMEZONES } from "@/lib/event-timezone";
import { cn } from "@/lib/utils";
import {
  PanelFieldLabel,
  PanelSectionHeading,
  panelAccentDot,
  panelBody,
  panelControl,
  panelHint,
  panelPage,
  panelPill,
  panelSurface,
  panelTitle,
} from "@/components/producer/panel";

export function InfoStep({
  form,
  updateField,
  cropImageSrc,
  coverPreview,
  onCropDone,
  onCropCancel,
  onCoverSelect,
  eventId,
  isEndDateInvalid,
  selectedTimeZoneLabel,
  organizerSlug,
}: {
  form: EventFormValues;
  updateField: (f: keyof EventFormValues, v: EventFormValues[keyof EventFormValues]) => void;
  cropImageSrc: string;
  coverPreview: string | null;
  onCropDone: (file: File) => void;
  onCropCancel: () => void;
  onCoverSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  eventId?: string;
  isEndDateInvalid: boolean;
  selectedTimeZoneLabel: string;
  organizerSlug?: string | null;
}) {
  const { t, i18n } = useTranslation("producerEventForm");
  const { categories } = useEventCategories(form.category);

  return (
    <div className={panelPage}>
      <header>
        <h1 className={panelTitle}>{t("form.steps.info.label")}</h1>
      </header>

      <div className={panelSurface}>
        <PanelSectionHeading>{t("form.sections.info.title")}</PanelSectionHeading>
        <div className={panelBody}>
          <div>
            <PanelFieldLabel>{t("form.sections.info.eventName")}</PanelFieldLabel>
            <Input
              value={form.title}
              onChange={(e) => updateField("title", e.target.value)}
              placeholder={t("form.sections.info.eventNamePlaceholder")}
              maxLength={100}
              className={panelControl}
            />
            <p className={cn(panelHint, "tabular-nums")}>
              {t("form.sections.info.charactersRemaining", { count: 100 - form.title.length })}
            </p>
          </div>

          <div>
            <PanelFieldLabel>{t("form.sections.info.customUrl")}</PanelFieldLabel>
            <div className="mt-2 flex min-h-12 flex-col overflow-hidden rounded-2xl bg-secondary/50 transition-[background-color,box-shadow] duration-150 ease-out focus-within:bg-background focus-within:ring-1 focus-within:ring-primary/30 sm:flex-row sm:items-center">
              <span className="truncate px-4 pt-2.5 text-[11px] text-muted-foreground sm:pt-0 sm:pr-0">
                tickethall.com{getEventPublicPath("", organizerSlug)}
              </span>
              <Input
                value={form.slug}
                onChange={(e) => updateField("slug", e.target.value)}
                className="h-11 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
          </div>

          <div>
            <PanelFieldLabel>{t("form.sections.info.coverImage")}</PanelFieldLabel>
            {cropImageSrc ? (
              <div className="mt-2 overflow-hidden rounded-[24px] border border-border/60 bg-muted/30 p-4">
                <InlineImageCrop
                  imageSrc={cropImageSrc}
                  onCropDone={onCropDone}
                  onCancel={onCropCancel}
                />
              </div>
            ) : (
              <div className={cn(
                "mt-2 overflow-hidden rounded-[24px] border-2 border-dashed transition-colors",
                coverPreview ? "border-border" : "border-border hover:border-foreground/25",
              )}>
                {coverPreview ? (
                  <div className="relative">
                    <img src={coverPreview} alt="Capa do evento" className="w-full aspect-[1600/838] object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-200 ease-out hover:opacity-100">
                      <label className={cn(
                        "inline-flex cursor-pointer items-center gap-2 bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/80",
                        panelPill,
                      )}>
                        <input type="file" accept="image/*" onChange={onCoverSelect} className="hidden" />
                        <Upload className="h-4 w-4" />{t("form.sections.info.changeImage")}
                      </label>
                    </div>
                  </div>
                ) : (
                  <label className="flex cursor-pointer flex-col items-center justify-center py-10">
                    <input type="file" accept="image/*" onChange={onCoverSelect} className="hidden" />
                    <p className="text-sm font-medium text-foreground">
                      <span className="hidden sm:inline">{t("form.sections.info.uploadDesktop")}</span>
                      <span className="sm:hidden">{t("form.sections.info.uploadMobile")}</span>
                    </p>
                    <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-secondary/50 px-3 py-1">
                      <span className={panelAccentDot} aria-hidden />
                      <p className="text-center text-[11px] font-medium tabular-nums text-foreground">1600 × 838</p>
                    </div>
                    <p className="mt-2 px-4 text-center text-[11px] text-muted-foreground">
                      {t("form.sections.info.imageFormats")}
                    </p>
                  </label>
                )}
              </div>
            )}
          </div>

          <div>
            <PanelFieldLabel>{t("form.sections.info.category")}</PanelFieldLabel>
            <Select value={form.category} onValueChange={(v) => updateField("category", v)}>
              <SelectTrigger className={panelControl}><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                {categories.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{getCategoryLabel(c.value, i18n.language)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <PanelFieldLabel>{t("form.sections.info.descriptionLabel")}</PanelFieldLabel>
            <div className="mt-2">
              <RichTextEditor
                variant="panel"
                value={form.description}
                onChange={(val) => updateField("description", val)}
              />
            </div>
          </div>

          <div className="border-t border-border/60 pt-6">
            {eventId ? (
              <EventAttachmentsManager eventId={eventId} />
            ) : (
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">{t("form.sections.info.attachments")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("form.sections.info.attachmentsHint")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={panelSurface}>
        <PanelSectionHeading>{t("form.sections.datetime.title")}</PanelSectionHeading>
        <div className={panelBody}>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <PanelFieldLabel>{t("form.sections.datetime.startDate")}</PanelFieldLabel>
              <DateTimePicker
                variant="panel"
                value={form.start_date}
                onChange={(val) => updateField("start_date", val)}
              />
            </div>
            <div>
              <PanelFieldLabel className={isEndDateInvalid ? "text-destructive" : undefined}>
                {t("form.sections.datetime.endDate")}
              </PanelFieldLabel>
              <DateTimePicker
                variant="panel"
                value={form.end_date}
                onChange={(val) => updateField("end_date", val)}
                className={isEndDateInvalid ? "border-destructive/50" : undefined}
              />
              {isEndDateInvalid && (
                <p className="mt-1.5 text-[10px] font-medium text-destructive">
                  {t("form.sections.datetime.endDateError")}
                </p>
              )}
            </div>
          </div>
          <div>
            <PanelFieldLabel>{t("form.sections.datetime.timezone")}</PanelFieldLabel>
            <Select value={form.time_zone} onValueChange={(v) => updateField("time_zone", v)}>
              <SelectTrigger className={panelControl}>
                <SelectValue placeholder={t("form.sections.datetime.timezonePlaceholder")} />
              </SelectTrigger>
              <SelectContent className="rounded-2xl">
                {BRAZIL_EVENT_TIMEZONES.map((timeZone) => (
                  <SelectItem key={timeZone.value} value={timeZone.value}>
                    {timeZone.label} ({timeZone.offset})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className={panelHint}>
              {t("form.sections.datetime.timezoneHint", { timezone: selectedTimeZoneLabel })}
            </p>
          </div>
          {!form.is_online && (
            <div>
              <PanelFieldLabel>{t("form.sections.datetime.doorsOpen")}</PanelFieldLabel>
              <DateTimePicker
                variant="panel"
                value={form.doors_open_time}
                onChange={(val) => updateField("doors_open_time", val)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
